import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Search } from 'lucide-react';
import TierFormFields from './TierFormFields';
import { fetchTiers, updateTier } from './api';
import { AdminTier, formFromTier, TierFormValue } from './types';
import { primaryBtnClass, secondaryBtnClass } from '../styles';

const unlimited = (value: number | string | null) =>
  value == null || value === '' ? 'Unlimited' : String(value);

const EditTier: React.FC = () => {
  const [q, setSearch] = useState('');
  const [data, setData] = useState<AdminTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminTier | null>(null);
  const [value, setValue] = useState<TierFormValue | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchTiers({ q: q.trim() });
      setData(payload.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => {
    if (!selected || !value) return false;
    return JSON.stringify(formFromTier(selected)) !== JSON.stringify(value);
  }, [selected, value]);

  const backToList = () => {
    if (dirty && !window.confirm('You have unsaved changes. Go back to the list anyway?')) {
      return;
    }
    setSelected(null);
    setValue(null);
    setSuccess(null);
    setError(null);
    void load();
  };

  const save = async () => {
    if (!selected || !value) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateTier(selected.id, value);
      setSelected(updated);
      setValue(formFromTier(updated));
      setSuccess(`Saved ${updated.name}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  if (selected && value) {
    return (
      <div className="max-w-3xl">
        <button
          type="button"
          onClick={backToList}
          disabled={saving}
          className="inline-flex items-center gap-1 text-sm text-[#3da3d4] hover:text-[#2d8ab8] mb-4 disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to list
        </button>
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-4">Edit {selected.name}</h3>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {success}
          </div>
        )}
        <TierFormFields value={value} onChange={setValue} mode="edit" disabled={saving} />
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={() => void save()}
            className={primaryBtnClass}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Save changes
          </button>
          <button type="button" disabled={saving} onClick={backToList} className={secondaryBtnClass}>
            Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
          <input
            value={q}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load();
            }}
            placeholder="Search tiers"
            className="h-10 w-full rounded-md border border-[#D1D1D1] pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
          />
        </div>
        <button type="button" onClick={() => void load()} className={secondaryBtnClass}>
          Search
        </button>
      </div>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading tiers…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Max creators</th>
                <th className="px-3 py-2.5 font-semibold">Max viewers</th>
                <th className="px-3 py-2.5 font-semibold">Starting credits</th>
                <th className="px-3 py-2.5 font-semibold">Organizations</th>
                <th className="px-3 py-2.5 font-semibold">Active</th>
              </tr>
            </thead>
            <tbody>
              {data.map((tier) => (
                <tr
                  key={tier.id}
                  className="border-t border-[#E8E8E8] cursor-pointer hover:bg-[#FAFAFA]"
                  onClick={() => {
                    setSelected(tier);
                    setValue(formFromTier(tier));
                    setSuccess(null);
                    setError(null);
                  }}
                >
                  <td className="px-3 py-2.5 text-[#0C0B0B]">{tier.name}</td>
                  <td className="px-3 py-2.5">{unlimited(tier.max_creators)}</td>
                  <td className="px-3 py-2.5">{unlimited(tier.max_viewers)}</td>
                  <td className="px-3 py-2.5">{unlimited(tier.starting_credit_pool)}</td>
                  <td className="px-3 py-2.5">{tier.organization_count ?? '—'}</td>
                  <td className="px-3 py-2.5">
                    {tier.is_active ? (
                      <span className="text-emerald-700">Active</span>
                    ) : (
                      <span className="text-[#8A8A8A]">Inactive</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-[#8A8A8A]">
                    No tiers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EditTier;
